import { CalendarClockIcon, MapPinIcon, MoreHorizontalIcon, PencilIcon, PlayIcon, PlusIcon, RefreshCwIcon, SirenIcon, Trash2Icon, ZapIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { TableSkeleton } from '@/components/LoadingBlock';
import { SectionTitle } from '@/components/PageHeader';
import { PageIntro } from '@/components/PageIntro';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useJobContext } from '@/layouts/JobLayout';
import { errorMessage } from '@/lib/api';
import { fmtDateTime, fmtDateTimeInZone, fmtHourInZone, fmtRelative, truncate, tzAbbreviation } from '@/lib/format';
import type { AutomationOverview, AutomationRuleDto, AutomationRuleInput, WeatherCard } from '@/lib/types';
import { cn } from '@/lib/utils';
import { describeTrigger, weatherProviderLabel } from './describe';
import { RuleDialog } from './RuleDialog';
import { WeatherAlertList } from './WeatherAlerts';
import { useAutomation, useAutomationMutations, useAutomationRuns, useJobWeather } from './queries';

export function AutomationPage() {
  const { job } = useJobContext();
  const overview = useAutomation(job.id);
  const m = useAutomationMutations(job.id);
  const [dialog, setDialog] = useState<{ open: boolean; kind: 'weather' | 'schedule'; rule: AutomationRuleDto | null }>({ open: false, kind: 'weather', rule: null });
  const [deleting, setDeleting] = useState<AutomationRuleDto | null>(null);
  const { ruleId } = useParams<{ ruleId?: string }>();
  const navigate = useNavigate();
  // Deep link: /jobs/:jobId/automation/:ruleId opens that rule once the list is in.
  const linkedRule = ruleId ? (overview.data?.items ?? []).find((r) => r.id === ruleId) ?? null : null;
  useEffect(() => {
    if (linkedRule) setDialog({ open: true, kind: linkedRule.kind, rule: linkedRule });
  }, [linkedRule]);

  if (overview.isPending) return <TableSkeleton rows={4} cols={4} />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const o = overview.data;
  const weatherRules = o.items.filter((r) => r.kind === 'weather');
  const scheduleRules = o.items.filter((r) => r.kind === 'schedule');
  const hasLocation = o.location.latitude !== null && o.location.longitude !== null;
  const tz = o.location.timezone ?? o.quietHours.timezone;

  const submit = async (body: AutomationRuleInput, id: string | null) => {
    if (id) await m.update.mutateAsync({ id, body });
    else await m.create.mutateAsync(body);
  };

  const toggle = async (r: AutomationRuleDto) => {
    try {
      await m.update.mutateAsync({ id: r.id, body: { enabled: !r.enabled } });
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const evaluate = async (r: AutomationRuleDto, force = false) => {
    try {
      const res = await m.evaluate.mutateAsync({ id: r.id, force });
      if (res.fired) toast.success(res.fired.outcome === 'drafted' ? 'Draft created — review it under History → Upcoming' : res.fired.outcome === 'sent' ? 'Message sent' : `Not sent: ${res.fired.reason ?? res.fired.outcome}`);
      else if (res.skipped) toast.message(res.skipped);
      else toast.message('Condition not met right now — nothing sent.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <PageIntro title="Automation" description="Site-aware notices tied to the job address: weather triggers, daily start reminders, timesheet nudges. Drafts wait for a job admin; automatic sends go out on their own." />

      {!o.features.weatherAutomation && !o.features.scheduledSends ? (
        <EmptyState icon={ZapIcon} title="Automation is turned off" description="Your organization has disabled weather triggers and scheduled sends. An organization admin can turn them on under Organization → Settings." />
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <LocationCard o={o} jobId={job.id} canEdit={o.canManage} />
            {o.features.weatherAutomation && <WeatherNow jobId={job.id} enabled={hasLocation} tz={tz} lookahead={o.defaults.lookaheadHours} onRefresh={() => m.refreshWeather.mutate()} refreshing={m.refreshWeather.isPending} />}
          </div>

          {o.features.scheduledSends && (
            <section className="space-y-3">
              <SectionTitle
                actions={
                  o.canManage ? (
                    <Button size="sm" onClick={() => setDialog({ open: true, kind: 'schedule', rule: null })}>
                      <PlusIcon /> Recurring reminder
                    </Button>
                  ) : undefined
                }
              >
                Recurring reminders
              </SectionTitle>
              <p className="text-muted-foreground -mt-1 text-xs">
                One-off scheduled messages live in <Link to={`/jobs/${job.id}/history?view=upcoming`} className="underline">History → Upcoming</Link>; schedule them from Compose.
              </p>
              {scheduleRules.length === 0 ? (
                <EmptyState icon={CalendarClockIcon} title="No recurring reminders" description="Daily start-time reminders, Friday timesheet nudges, inspection-day notices — set once, sent in the job's local time." className="py-10" />
              ) : (
                <RuleTable rules={scheduleRules} tz={tz} canManage={o.canManage} onEdit={(r) => setDialog({ open: true, kind: 'schedule', rule: r })} onDelete={setDeleting} onToggle={toggle} onEvaluate={evaluate} busy={m.update.isPending || m.evaluate.isPending} />
              )}
            </section>
          )}

          {o.features.weatherAutomation && (
            <section className="space-y-3">
              <SectionTitle
                actions={
                  o.canManage ? (
                    <Button size="sm" onClick={() => setDialog({ open: true, kind: 'weather', rule: null })} disabled={!hasLocation} title={hasLocation ? undefined : 'Add the job address first'}>
                      <PlusIcon /> Weather rule
                    </Button>
                  ) : undefined
                }
              >
                Weather rules
              </SectionTitle>
              {weatherRules.length === 0 ? (
                <EmptyState icon={SirenIcon} title="No weather rules yet" description="Official tornado, flood, severe-storm, hurricane and winter alerts, plus rain, lightning, heat index and wind thresholds, prompt (or auto-send) a delay or safety notice. Heat rules double as a compliance record." className="py-10" />
              ) : (
                <RuleTable rules={weatherRules} tz={tz} canManage={o.canManage} onEdit={(r) => setDialog({ open: true, kind: 'weather', rule: r })} onDelete={setDeleting} onToggle={toggle} onEvaluate={evaluate} busy={m.update.isPending || m.evaluate.isPending} />
              )}
            </section>
          )}

          <Runs jobId={job.id} />
        </>
      )}

      <RuleDialog open={dialog.open} onOpenChange={(open) => {
          setDialog((s) => ({ ...s, open }));
          if (!open && ruleId) navigate(`/jobs/${job.id}/automation`, { replace: true });
        }} jobId={job.id} jobName={job.name} kind={dialog.kind} rule={dialog.rule} overview={o} onSubmit={submit} busy={m.create.isPending || m.update.isPending} />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete "${deleting?.name}"?`}
        description="Past runs and any drafts it created are kept."
        confirmLabel="Delete"
        destructive
        loading={m.remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await m.remove.mutateAsync(deleting.id);
            toast.success('Rule deleted');
            setDeleting(null);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

function LocationCard({ o, jobId, canEdit }: { o: AutomationOverview; jobId: string; canEdit: boolean }) {
  const has = o.location.latitude !== null;
  return (
    <section className="bg-card border p-5">
      <SectionTitle className="mb-3">Job site</SectionTitle>
      {has ? (
        <div className="grid gap-1 text-sm">
          <p className="flex items-start gap-2">
            <MapPinIcon className="text-muted-foreground mt-0.5 size-4 shrink-0" />
            <span>{o.location.address ?? 'Coordinates only'}</span>
          </p>
          <p className="text-muted-foreground pl-6 text-xs tabular-nums">
            {o.location.latitude?.toFixed(4)}, {o.location.longitude?.toFixed(4)} · {o.location.timezone ?? o.quietHours.timezone} ({tzAbbreviation(o.location.timezone ?? o.quietHours.timezone)})
          </p>
          <p className="text-muted-foreground pl-6 text-xs">
            Quiet hours {o.quietHours.enabled ? `${o.quietHours.start}–${o.quietHours.end} local` : 'off'}.
          </p>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">No address yet. Weather rules and local-time schedules need the site location.</p>
      )}
      {canEdit && (
        <Button asChild variant="outline" size="sm" className="mt-4">
          <Link to={`/jobs/${jobId}/settings#location`}>{has ? 'Change in Settings' : 'Add the job address'}</Link>
        </Button>
      )}
    </section>
  );
}

function WeatherNow({ jobId, enabled, tz, lookahead, onRefresh, refreshing }: { jobId: string; enabled: boolean; tz: string; lookahead: number; onRefresh: () => void; refreshing: boolean }) {
  // Alerts are listed above the numbers so a warning is the first thing on the card.
  const q = useJobWeather(jobId, enabled);
  const f: WeatherCard | null | undefined = q.data?.forecast;
  return (
    <section className="bg-card border p-5">
      <SectionTitle
        className="mb-3"
        actions={
          enabled ? (
            <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing}>
              <RefreshCwIcon className={cn(refreshing && 'animate-spin')} /> Refresh
            </Button>
          ) : undefined
        }
      >
        Forecast · next {lookahead}h
      </SectionTitle>
      {!enabled ? (
        <p className="text-muted-foreground text-sm">Add the job address to see the forecast the rules use.</p>
      ) : q.isPending ? (
        <Skeleton className="h-28 w-full" />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Could not load the forecast" />
      ) : !f ? (
        <p className="text-muted-foreground text-sm">{q.data?.reason ?? 'No forecast available.'}</p>
      ) : (
        <div className="grid gap-3">
          <WeatherAlertList alerts={f.alerts ?? []} tz={tz} />
          <div className="flex items-start gap-4">
            <div>
              <div className="text-3xl font-semibold tabular-nums">{Math.round(f.current?.temperatureF ?? 0)}°F</div>
              <div className="text-muted-foreground text-xs">
                feels like {Math.round(f.current?.heatIndexF ?? 0)}° · {f.current?.humidity ?? 0}% humidity
              </div>
            </div>
            <p className="text-sm leading-snug">{f.summary}</p>
          </div>
          <div className="grid grid-cols-6 gap-px border bg-border sm:grid-cols-12">
            {f.hourly.slice(0, 12).map((h) => (
              <div key={h.time} className={cn('bg-card flex flex-col items-center gap-0.5 py-1.5 text-[11px]', h.thunderstorm && 'bg-warning/15')}>
                <span className="text-muted-foreground">{fmtHourInZone(h.time, tz)}</span>
                <span className="font-medium tabular-nums">{Math.round(h.temperatureF)}°</span>
                <span className={cn('tabular-nums', h.precipitationProbability >= 50 ? 'text-primary font-medium' : 'text-muted-foreground')}>{h.precipitationProbability}%</span>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-[11px]">
            {weatherProviderLabel(f.provider)}. Updated {fmtRelative(f.fetchedAt)}.
            {f.alertsAvailable !== false && (f.alerts?.length ?? 0) === 0 ? ' No alerts in force.' : ''}
          </p>
        </div>
      )}
    </section>
  );
}

function RuleTable({ rules, tz, canManage, onEdit, onDelete, onToggle, onEvaluate, busy }: { rules: AutomationRuleDto[]; tz: string; canManage: boolean; onEdit: (r: AutomationRuleDto) => void; onDelete: (r: AutomationRuleDto) => void; onToggle: (r: AutomationRuleDto) => void; onEvaluate: (r: AutomationRuleDto, force?: boolean) => void; busy: boolean }) {
  const menu = (r: AutomationRuleDto) =>
    canManage ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${r.name}`}>
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(r)}>
            <PencilIcon /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onEvaluate(r, r.kind === 'weather')}>
            <PlayIcon /> {r.kind === 'weather' ? 'Evaluate now' : 'Run now'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={() => onDelete(r)}>
            <Trash2Icon /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : null;
  const badges = (r: AutomationRuleDto) => (
    <div className="flex flex-wrap gap-1">
      <Badge variant={r.mode === 'auto' ? 'default' : 'outline'}>{r.mode === 'auto' ? 'Auto-send' : 'Draft'}</Badge>
      {r.action.audience.type === 'tags' && <Badge variant="secondary">{r.action.audience.tags.join(', ')}</Badge>}
      {r.bypassQuietHours && <Badge variant="warning">Ignores quiet hours</Badge>}
    </div>
  );
  return (
    <>
    <ul className="bg-card divide-y border md:hidden" aria-label="Rules">
      {rules.map((r) => (
        <li key={r.id} className={cn('grid gap-2 px-4 py-3', !r.enabled && 'opacity-60')}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium">{r.name}</div>
              <div className="text-muted-foreground text-xs">{describeTrigger(r)}</div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Switch checked={r.enabled} disabled={!canManage || busy} onCheckedChange={() => onToggle(r)} aria-label={`Toggle ${r.name}`} />
              {menu(r)}
            </div>
          </div>
          {badges(r)}
          <p className="text-muted-foreground text-xs">
            {r.kind === 'schedule' ? `Next ${r.nextRunAt ? fmtDateTimeInZone(r.nextRunAt, tz) : '—'}` : `Checked ${r.lastEvaluatedAt ? fmtRelative(r.lastEvaluatedAt) : 'not yet'}`} · Last fired {r.lastFiredAt ? fmtRelative(r.lastFiredAt) : 'never'}
          </p>
        </li>
      ))}
    </ul>
    <div className="bg-card hidden overflow-x-auto border md:block">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Rule</TableHead>
            <TableHead>Condition</TableHead>
            <TableHead>Action</TableHead>
            <TableHead>{rules[0]?.kind === 'schedule' ? 'Next run' : 'Last checked'}</TableHead>
            <TableHead>Last fired</TableHead>
            <TableHead>On</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rules.map((r) => (
            <TableRow key={r.id} className={!r.enabled ? 'opacity-60' : undefined}>
              <TableCell>
                <div className="font-medium">{r.name}</div>
                <div className="text-muted-foreground max-w-xs truncate text-xs" title={r.action.template}>
                  {truncate(r.action.template, 80)}
                </div>
              </TableCell>
              <TableCell className="text-sm">{describeTrigger(r)}</TableCell>
              <TableCell>{badges(r)}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{r.kind === 'schedule' ? (r.nextRunAt ? fmtDateTimeInZone(r.nextRunAt, tz) : '—') : r.lastEvaluatedAt ? fmtRelative(r.lastEvaluatedAt) : 'Not yet'}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{r.lastFiredAt ? fmtRelative(r.lastFiredAt) : '—'}</TableCell>
              <TableCell>
                <Switch checked={r.enabled} disabled={!canManage || busy} onCheckedChange={() => onToggle(r)} aria-label={`Toggle ${r.name}`} />
              </TableCell>
              <TableCell>{menu(r)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    </>
  );
}

function Runs({ jobId }: { jobId: string }) {
  const runs = useAutomationRuns(jobId);
  const [all, setAll] = useState(false);
  const items = runs.data?.items ?? [];
  if (items.length === 0) return null;
  const shown = all ? items.slice(0, 50) : items.slice(0, 6);
  return (
    <section className="space-y-3">
      <SectionTitle actions={<span className="text-muted-foreground text-xs">Every check the rules made, newest first</span>}>Recent runs</SectionTitle>
      <div className="bg-card border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Rule</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead className="hidden md:table-cell">Detail</TableHead>
              <TableHead className="hidden sm:table-cell" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-muted-foreground">{fmtDateTime(r.createdAt)}</TableCell>
                <TableCell className="font-medium">{r.ruleName ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={r.outcome === 'sent' ? 'success' : r.outcome === 'drafted' ? 'secondary' : r.outcome === 'failed' ? 'danger' : 'muted'}>{r.outcome}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden max-w-md truncate text-xs md:table-cell" title={r.reason ?? (typeof r.snapshot['body'] === 'string' ? (r.snapshot['body'] as string) : undefined)}>
                  {r.reason ?? (r.snapshot['evaluation'] as { detail?: string } | undefined)?.detail ?? (typeof r.snapshot['body'] === 'string' ? truncate(r.snapshot['body'] as string, 100) : '')}
                </TableCell>
                <TableCell className="hidden text-right sm:table-cell">
                  {r.messageId && (
                    <Button asChild variant="ghost" size="xs">
                      <Link to={`/jobs/${jobId}/history/${r.messageId}`}>Open message</Link>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items.length > 6 && (
          <div className="flex justify-center border-t py-1.5">
            <Button type="button" variant="ghost" size="xs" onClick={() => setAll((v) => !v)}>
              {all ? 'Show fewer' : `Show all ${Math.min(items.length, 50)}`}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
